// Rechten F2 (issue #23, spec §4): knoppen volgen rol × niveau × bereik uit auth/me, nooit meer een
// Entra-app-rol (die bestaan sinds F5 niet meer). Waarom ook een regel per actie hier: auth/me zegt enkel
// ja/nee; om een knop te VERGRENDELEN met uitleg ("Vraagt niveau Goedkeuren", "Activeer eerst Beheer") moet de
// webapp weten welke rol en welk niveau de actie vraagt. De regels spiegelen AuthMe.Capabilities en de
// rechtenmatrix; bij verschil wint de servervlag (capabilities.<pad>) altijd.

export const LEVELS = ["L", "B", "U", "G"];
export const LEVEL_NAMES = { L: "Lezen", B: "Bewerken", U: "Uitvoeren", G: "Goedkeuren" };

export function levelRank(code) {
  return LEVELS.indexOf(String(code || "").toUpperCase());
}

export function levelName(code) {
  return LEVEL_NAMES[String(code || "").toUpperCase()] || String(code || "");
}

// key = pad in auth/me `capabilities` (bv. "contracts.approve"); roles = één van die rollen volstaat (zoals
// "A|B" in de API); requireAll = de operatie achter de knop is RequiresAll (bereik Alles nodig). Issue #23 (F2, D6):
// rol, niveau en requireAll volgen exact de operatie uit AuthMe.CapabilityOperations; een .NET-test bewaakt dat.
// server = false: auth/me heeft (nog) geen vlag; dan beslist enkel de regel op access.roles.
export const CAPABILITY_RULES = {
  "assets.write": { roles: ["STAMDATA"], level: "B", requireAll: true },
  "lookups.write": { roles: ["STAMDATA"], level: "B", requireAll: true },
  "banks.write": { roles: ["STAMDATA"], level: "B", requireAll: true },
  "legalEntities.write": { roles: ["STAMDATA"], level: "B", requireAll: true },
  "marketDataBootstrap.write": { roles: ["STAMDATA"], level: "U", requireAll: true },
  "bankImport.write": { roles: ["BANK"], level: "B", requireAll: true },
  "bankImport.run": { roles: ["BANK"], level: "U", requireAll: true },
  "bankProfiles.write": { roles: ["BANK"], level: "B", requireAll: true },
  "bankProfiles.approve": { roles: ["BANK"], level: "G", requireAll: true },
  "exact.write": { roles: ["BOEKHOUDING"], level: "U", requireAll: true },
  "exact.approve": { roles: ["BOEKHOUDING"], level: "G", requireAll: true },
  "liquidity.write": { roles: ["LIQUIDITEIT"], level: "B", requireAll: true },
  "liquidity.approve": { roles: ["LIQUIDITEIT"], level: "G", requireAll: true },
  "liquidity.regenerate": { roles: ["LIQUIDITEIT"], level: "U", requireAll: true },
  "saa.write": { roles: ["BELEID"], level: "B", requireAll: true },
  "saa.approve": { roles: ["BELEID"], level: "G", requireAll: true },
  "contracts.write": { roles: ["CONTRACT"], level: "B" },
  "contracts.approve": { roles: ["CONTRACT"], level: "G", requireAll: true },
  "access.manage": { roles: ["BEHEER"], level: "B" },
  "feedback.triage": { roles: ["BEHEER"], level: "B" },
  // Zonder eigen vlag in auth/me (matrix D02 / D11): enkel de regel op access.roles.
  "bankDocuments.upload": { roles: ["BANK"], level: "B", requireAll: true, server: false },
  "bankDocuments.backup": { roles: ["BANK", "BOEKHOUDING"], level: "L", server: false },
  "engineQueue.clear": { roles: ["BEHEER"], level: "U", server: false },
  // Issue #23 (F4 §4/§5): audit lezen = Beheer (geactiveerd) of Vermogen met bereik Alles (BEHEER heeft altijd bereik
  // Alles); signaal op "gezien" zetten = Beheer, Bewerken.
  "audit.read": { roles: ["BEHEER", "VERMOGEN"], level: "L", requireAll: true, server: false },
  "audit.ack": { roles: ["BEHEER"], level: "B", server: false },
};

const ROLE_NAMES = {
  VERMOGEN: "Vermogen bekijken",
  STAMDATA: "Stamdata & koersen",
  BANK: "Bankverwerking",
  BOEKHOUDING: "Boekhouding (Exact)",
  LIQUIDITEIT: "Liquiditeit & planning",
  BELEID: "Beleggingsbeleid (SAA)",
  CONTRACT: "Contracten & fees",
  BEHEER: "Beheer",
};

function readPath(obj, path) {
  let cur = obj;
  for (const part of String(path).split(".")) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = cur[part];
  }
  return typeof cur === "boolean" ? cur : undefined;
}

function toMs(value) {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

// Telt deze toewijzing NU? Niet-activeerbaar = altijd; activeerbaar = enkel met een lopende activatie. De klok
// van de browser telt mee: een activatie die verloopt terwijl de pagina open staat, vergrendelt meteen.
export function grantIsActive(grant, now = Date.now()) {
  if (!grant) return false;
  if (!grant.isActivatable) return grant.active !== false;
  const exp = toMs(grant.activationExpiresAt);
  return exp != null && exp > now;
}

function grantsOf(access, code) {
  const roles = Array.isArray(access?.roles) ? access.roles : [];
  return roles.filter((r) => String(r?.code || "").toUpperCase() === code);
}

// Het maximumniveau van een toewijzing; leeg = G (kolomdefault). Issue #23 (F5): geen bron Entra meer.
function maxLevelOf(access, grant) {
  return String(grant?.maxLevel || "G").toUpperCase();
}

// Het niveau waarmee de gebruiker de rol NU houdt (null = niet, of enkel activeerbaar zonder activatie).
export function heldLevel(access, roleCode, now = Date.now()) {
  let best = null;
  for (const g of grantsOf(access, String(roleCode || "").toUpperCase())) {
    if (!grantIsActive(g, now)) continue;
    const lvl = maxLevelOf(access, g);
    if (best == null || levelRank(lvl) > levelRank(best)) best = lvl;
  }
  return best;
}

// Beoordeling van één rol: null = toegestaan, anders de reden (role | activation | level | scope).
function judgeRole(access, code, level, requireAll, now) {
  const grants = grantsOf(access, code);
  if (grants.length === 0) return { reason: "role", code };
  const need = levelRank(level);
  const active = grants.filter((g) => grantIsActive(g, now));
  const activeOk = active.filter((g) => levelRank(maxLevelOf(access, g)) >= need);
  if (activeOk.length > 0) {
    if (!requireAll || activeOk.some((g) => g?.scope?.all !== false)) return null;
    return { reason: "scope", code };
  }
  // Een activeerbare toewijzing met voldoende niveau die nog niet loopt: activeren lost het op.
  const waiting = grants.find((g) => g.isActivatable && !grantIsActive(g, now) && levelRank(maxLevelOf(access, g)) >= need);
  if (waiting) return { reason: "activation", code, idAssignment: waiting.idAssignment ?? null };
  return { reason: "level", code };
}

// Hoe dichter bij "kan het zelf oplossen", hoe hoger: activeren > niveau > bereik > rol.
const REASON_ORDER = ["activation", "level", "scope", "role"];

export function capabilityHint(result) {
  if (!result || result.allowed) return "";
  const role = ROLE_NAMES[result.roleCode] || result.roleCode || "";
  switch (result.reason) {
    case "activation":
      return `Activeer eerst ${role}`;
    case "level":
      return `Vraagt niveau ${levelName(result.level)}`;
    case "scope":
      return `Vraagt bereik Alles in ${role}`;
    case "role":
      return role ? `Vraagt de rol ${role}` : "Niet toegestaan met je rechten";
    default:
      return "Niet toegestaan met je huidige rechten";
  }
}

/**
 * Mag de gebruiker deze actie? { allowed, hidden, locked, reason, level, roleCode, idAssignment, hint }.
 * hidden = de gebruiker heeft geen enkele van de rollen (knop weg); locked = hij heeft de rol wel, maar mist
 * niveau, activatie of bereik (knop uitgegrijsd met uitleg in hint).
 */
export function evaluateCapability(auth, key, now = Date.now()) {
  const rule = CAPABILITY_RULES[key];
  const access = auth?.access || null;
  const serverFlag = rule?.server === false ? undefined : readPath(auth?.capabilities, key);
  if (!rule) {
    const allowed = Boolean(serverFlag);
    return { allowed, hidden: !allowed, locked: false, reason: allowed ? null : "role", level: null, roleCode: null, idAssignment: null, hint: "" };
  }
  const verdicts = rule.roles.map((code) => judgeRole(access, code, rule.level, Boolean(rule.requireAll), now));
  const clientAllowed = verdicts.some((v) => v === null);
  // De servervlag wint; een verlopen activatie (klok) vergrendelt wel meteen, ook vóór de volgende auth/me.
  const expiredSinceLoad = serverFlag === true && !clientAllowed && verdicts.some((v) => v?.reason === "activation");
  const allowed = serverFlag === undefined ? clientAllowed : serverFlag && !expiredSinceLoad;
  if (allowed) {
    return { allowed: true, hidden: false, locked: false, reason: null, level: rule.level, roleCode: null, idAssignment: null, hint: "" };
  }
  const best = verdicts
    .filter(Boolean)
    .sort((a, b) => REASON_ORDER.indexOf(a.reason) - REASON_ORDER.indexOf(b.reason))[0] || { reason: "denied", code: rule.roles[0] };
  const result = {
    allowed: false,
    hidden: best.reason === "role",
    locked: best.reason !== "role",
    reason: best.reason,
    level: rule.level,
    roleCode: best.code,
    idAssignment: best.idAssignment ?? null,
  };
  return { ...result, hint: capabilityHint(result) };
}

// De eigen activeerbare toewijzingen (voor de topbalk en de knop Activeren): { idAssignment, code, name,
// running, expiresAt, maxLevel }.
export function activatableRoles(access, now = Date.now()) {
  const roles = Array.isArray(access?.roles) ? access.roles : [];
  return roles
    .filter((r) => r?.isActivatable && r.idAssignment != null)
    .map((r) => ({
      idAssignment: r.idAssignment,
      code: String(r.code || "").toUpperCase(),
      name: r.name || ROLE_NAMES[String(r.code || "").toUpperCase()] || r.code,
      running: grantIsActive(r, now),
      expiresAt: r.activationExpiresAt || null,
      maxLevel: String(r.maxLevel || "G").toUpperCase(),
    }));
}

// "1 u 25 min" / "12 min" / "< 1 min" — resterende tijd van een activatie.
export function formatRemaining(expiresAt, now = Date.now()) {
  const exp = toMs(expiresAt);
  if (exp == null) return "";
  const mins = Math.floor((exp - now) / 60000);
  if (mins < 1) return "< 1 min";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h} u ${String(m).padStart(2, "0")} min` : `${m} min`;
}

// De eerstvolgende einde van een lopende activatie (ms) of null: daar herlaadt de webapp auth/me.
export function nextActivationExpiry(access, now = Date.now()) {
  const times = activatableRoles(access, now)
    .filter((r) => r.running)
    .map((r) => toMs(r.expiresAt))
    .filter((t) => t != null && t > now);
  return times.length ? Math.min(...times) : null;
}
